import every from 'lodash/every';
import find from 'lodash/find';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import moment from 'moment-timezone';

import {
  IAutoPolicy,
  TPolicyVehicleFormValues,
  IPolicyVehicle,
  TVehicleVariables,
  TAZVehicleVariables,
  TSCVehicleVariables,
  TGenericVehicleVariables,
} from '../../../interfaces';
import getAdditionalInterest from '../getAdditionalInterestObject';
import getLienholderObject from '../getLienholderObject';
import getUpdatedVehicleVariables from '../getUpdatedVehicleVariables';

export type TUpdateVehicleOnPolicy = {
  vehicleToUpdate: IPolicyVehicle;
  updatedData: TPolicyVehicleFormValues | null;
  isDeleting?: boolean | null;
  isUndoDelete?: boolean;
};

export function getPolicyVehicleVinBlacklist(
  editingVehicleVin: string,
  stagedPolicy: IAutoPolicy
) {
  const vehiclesList = stagedPolicy.details.vehicles || [];
  const vinsList = vehiclesList.map((vehicle) => vehicle.vin);
  return vinsList.filter((vin) => vin !== editingVehicleVin);
}

function getRegionSpecificPolicyVehicleVariables(
  values: TPolicyVehicleFormValues,
  regionCode: string,
  currentVehicle?: IPolicyVehicle
): {
  anti_theft_device?: boolean | null;
  rideshare_use: boolean;
  variables: TVehicleVariables;
} {
  const isOwned = values.ownership_status === 'owned';
  const sharedVariables = {
    collision_deductible: values.collision_deductible,
    comprehensive_deductible: values.collision_deductible,
    towing_road_services: values.towing_road_services,
    oem_parts: values.oem_parts,
    new_car_replacement: values.new_car_replacement,
  };
  switch (regionCode) {
    case 'IL':
    case 'OH':
    case 'IN':
    case 'TN':
    case 'MO':
      return {
        rideshare_use: values.side_hustle!,
        anti_theft_device: values.anti_theft_device!,
        variables: {
          ...currentVehicle?.variables,
          ...sharedVariables,
          temporary_ride: values.temporary_ride!,
          gap: isOwned ? false : values.gap!,
          side_hustle: values.side_hustle!,
          uninsured_motorist_property_damage_limit:
            values.uninsured_motorist_property_damage_limit!,
        } as TGenericVehicleVariables,
      };
    case 'AZ':
      return {
        rideshare_use: values.side_hustle!,
        variables: {
          ...currentVehicle?.variables,
          ...sharedVariables,
          temporary_ride: values.temporary_ride!,
          gap: isOwned ? false : values.gap!,
          side_hustle: values.side_hustle!,
          glass: values.glass!,
        } as TAZVehicleVariables,
      };

    case 'SC':
    default:
      return {
        rideshare_use: values.ridesharing!,
        variables: {
          ...currentVehicle?.variables,
          ...sharedVariables,
          loss_of_use: values.loss_of_use!,
          loan_lease_payoff: isOwned ? false : values.loan_lease_payoff!,
          ridesharing: values.ridesharing!,
        } as TSCVehicleVariables,
      };
  }
}

export function getVehicleFromValues(
  values: TPolicyVehicleFormValues,
  stagedPolicy: IAutoPolicy,
  currentVehicle?: IPolicyVehicle
): IPolicyVehicle {
  const additional_interest = getAdditionalInterest(
    values.interested_party_name,
    values.interested_party_address,
    currentVehicle?.additional_interest || null
  );

  if (currentVehicle) {
    return {
      ...currentVehicle,
      vin: values.vin,
      year: Number(values.year),
      make: values.make.toUpperCase(),
      model: values.model.toUpperCase(),
      style: values.style.toUpperCase(),
      ...(values?.trim && {
        trim: String(values.trim).toUpperCase(),
      }),
      automatic_emergency_braking: values.automatic_emergency_braking,
      ownership_status: values.ownership_status,
      years_owned_financed_leased: values.years_owned_financed_leased,
      lienholder: getLienholderObject(
        {
          lienholder_name: values?.lienholder_name || '',
          address_line1: values?.address_line1 || '',
          address_line2: values?.address_line2 || '',
          address_city: values?.address_city || '',
          address_postal: values?.address_postal || '',
          address_region: values?.address_region || '',
        },
        { isPolicyUpdate: true }
      ),
      business_use: values.business_use,
      anti_theft_device: values.anti_theft_device,
      rideshare_use:
        !!values.rideshare_use ||
        !!currentVehicle.variables.side_hustle ||
        !!currentVehicle.variables.ridesharing,
      variables: getUpdatedVehicleVariables({
        year: Number(values.year),
        ownership_status: values.ownership_status,
        variables: values.variables || currentVehicle.variables,
        garage_address: currentVehicle.garage_address,
        baseVehicleYear: Number(moment(stagedPolicy.starts_at).format('YYYY')),
      }) as TVehicleVariables,
      additional_interest,
    };
  }
  return {
    data_source: 'manual',
    garage_address: omit(stagedPolicy.rating_address, [
      'id',
      'latitude',
      'longitude',
    ]),
    id: Number(values.id) || null,
    vin: values.vin,
    year: Number(values.year),
    make: values.make.toUpperCase(),
    model: values.model.toUpperCase(),
    style: values.style.toUpperCase(),
    ...(values?.trim && {
      trim: String(values.trim).toUpperCase(),
    }),
    automatic_emergency_braking: values.automatic_emergency_braking,
    ownership_status: values.ownership_status,
    years_owned_financed_leased: values.years_owned_financed_leased,
    lienholder: getLienholderObject(
      {
        lienholder_name: values?.lienholder_name || '',
        address_line1: values?.address_line1 || '',
        address_line2: values?.address_line2 || '',
        address_city: values?.address_city || '',
        address_postal: values?.address_postal || '',
        address_region: values?.address_region || '',
      },
      { isPolicyUpdate: true }
    ),
    business_use: values.business_use,
    anti_theft_device: values.anti_theft_device,
    additional_interest,
    ...getRegionSpecificPolicyVehicleVariables(
      values,
      stagedPolicy.rating_address.region.code
    ),
  };
}

export function addNewVehicleToPolicy(
  values: TPolicyVehicleFormValues,
  stagedPolicy: IAutoPolicy
): IAutoPolicy {
  const newVehicle = omit(getVehicleFromValues(values, stagedPolicy), 'id');

  const newVehiclesList = [...stagedPolicy.details.vehicles, newVehicle];

  const updatedPolicy: IAutoPolicy = {
    ...stagedPolicy,
    details: {
      ...stagedPolicy.details,
      vehicles: newVehiclesList,
    },
  };

  return updatedPolicy;
}

export function updateVehicleOnPolicy(
  {
    vehicleToUpdate,
    updatedData = null,
    isDeleting = null,
    isUndoDelete = false,
  }: TUpdateVehicleOnPolicy,
  stagedPolicy: IAutoPolicy
) {
  const updatesToMake = !updatedData
    ? {}
    : getVehicleFromValues(updatedData, stagedPolicy, vehicleToUpdate);

  const stagedVehiclesList = stagedPolicy.details.vehicles;

  const isVehicleSavedToPolicy = !!vehicleToUpdate?.id;

  const updatedVehiclesList =
    isDeleting && !isVehicleSavedToPolicy
      ? stagedVehiclesList.filter(
          (vehicle) => vehicle.vin !== vehicleToUpdate?.vin
        )
      : stagedVehiclesList.map((vehicle) => {
          if (vehicleToUpdate?.vin === vehicle.vin) {
            const updatedVehicle = {
              ...vehicle,
              ...updatesToMake,
            };

            if (!!vehicle.lienholder && !updatedVehicle.lienholder) {
              updatedVehicle.lienholder = {
                ...vehicle.lienholder,
                deleted: true,
              };
            }

            if (isDeleting) {
              return {
                ...updatedVehicle,
                deleted: true,
              };
            }
            return omit(updatedVehicle, ['deleted']);
          }
          return vehicle;
        });

  const hasNoChanges =
    !isDeleting &&
    !isUndoDelete &&
    every(
      updatedVehiclesList.map((vehicle) => {
        const oldVehicle = find(stagedPolicy.details.vehicles, {
          vin: vehicle.vin,
        });
        const oldLienholder = get(oldVehicle, 'lienholder')
          ? {
              ...omit(oldVehicle?.lienholder, [
                'deleted',
                'address.id',
                'address.country.id',
                'address.region.id',
                'address.timezone',
                'address.region.name',
                'address.county',
              ]),
            }
          : null;
        const newLienholder = vehicle?.lienholder
          ? {
              ...vehicle.lienholder,
              address: {
                ...omit(vehicle?.lienholder?.address, [
                  'region.name',
                  'county',
                ]),
                line2: vehicle?.lienholder?.address?.line2 || null,
              },
            }
          : null;

        return isEqual(
          {
            ...vehicle,
            lienholder: newLienholder,
          },
          {
            ...oldVehicle,
            lienholder: oldLienholder,
          }
        );
      }),
      (val) => val === true
    );

  const updatedPolicy = {
    ...stagedPolicy,
    details: {
      ...stagedPolicy.details,
      vehicles: updatedVehiclesList,
    },
  };

  return { updatedPolicy, hasNoChanges, isVehicleSavedToPolicy };
}
